import { FieldsMap } from "../constants";
import {
  HFieldProps,
  RuleObjectType,
  PageContent,
  TSectionRendererProps,
  RuleSimple,
  RuleOperators,
} from "../types";
import { HErrorMessageProps } from "../components";

export const OperatorsMap = {
  equal: "===",
  notEqual: "!==",
  greater: ">",
  greaterThan: ">=",
  less: "<",
  lessThan: "<=",
  includesSome: "",
  includesEvery: "",
  includesExact: "",
  empty: "",
  isNull: "isNull",
  notNull: "notNull",
  emptyString: "",
};

const defaultNoFormFields: Array<keyof typeof FieldsMap | undefined> = [
  "label",
  "openModalText",
  "disclaimer",
  "dividedList",
];

export const isSimpleRule = <V, R>(
  rule: RuleObjectType<V, R>,
): rule is RuleSimple<V, R> => {
  return "if" in rule;
};

type FormObject = Record<string, unknown>;
export const widgetIsFormField = (widget: HFieldProps) => {
  const isFormField = widget.noFormField !== true;
  const isControlFormField = !defaultNoFormFields.includes(widget.control);
  return isFormField && isControlFormField;
};
/*
 * Return all AJV validation rules from a list of Sections.
 * The returned object of rules will be passed to the form manager.
 * E.g. If a form field has a checkbox whose value is false, and we want to hide a related input field,
 * the validation rules associated with that input field are removed.
 * @param sections: TSectionRendererProps,
 * @param additionalProperties: whether to keep values that are not in the default
 */
export const getValidationObjectFromSections = (
  sections: TSectionRendererProps[],
  additionalProperties = true,
) => {
  const ifThenElse: Record<string, unknown>[] = [];
  const required: string[] = [];
  let atLeastOneIf = false;
  const properties = (sections || []).reduce((acc, curr) => {
    const widgetRules = (curr.widgets ?? []).reduce<Record<string, unknown>>(
      (wAcc, wCurr) => {
        const name = wCurr.name;
        const rule = { ...wCurr.rules };
        if (rule) {
          // grab all rules spreading all values expect for if/then/else
          const {
            if: excludedIf,
            then: excludedThen,
            else: excludedElse,
            ...remainingRules
          } = rule;
          const hasRuleOtherThanIf = Object.keys(remainingRules).length > 0;
          if (hasRuleOtherThanIf && !rule.if) {
            wAcc[name] = remainingRules;
          }
          if (rule.required || rule?.minLength === 1) {
            required.push(name);
          }

          // if there is a pattern in the rule we need to replace the escaping character.
          if (rule.pattern && wAcc[name]) {
            (wAcc[name] as FormObject).pattern = String(
              rule.pattern,
            ).replaceAll("\\", "\\\\");
          }
          if (rule.if) {
            const { if: ifRule, then: thenRule, else: elseRule } = rule;
            const conditionalRule = { if: ifRule } as Record<string, unknown>;
            if (elseRule) {
              conditionalRule.else = ifThenElse; //NOSONAR
            }
            if (thenRule) {
              conditionalRule.then = thenRule; //NOSONAR
            }
            ifThenElse.push(conditionalRule);
            atLeastOneIf = true;
          }
        }
        return wAcc;
      },
      {},
    );
    return { ...acc, ...widgetRules };
  }, {});
  let res: Record<string, unknown> = {
    type: "object",
    properties,
    required,
    $async: true,
    additionalProperties,
  };
  if (atLeastOneIf) {
    res = {
      ...res,
      allOf: ifThenElse,
    };
  }
  return res;
};

export const extractDefaultValues = (sections: TSectionRendererProps[]) => {
  return (sections || []).reduce((acc, curr) => {
    const widgetValues = (curr.widgets ?? [])
      .filter(w => widgetIsFormField(w))
      .reduce<Record<string, unknown>>((wAcc, wCurr) => {
        const name = wCurr.name;
        const value = wCurr.value;
        if (value) {
          wAcc[name] = value;
        }
        return wAcc;
      }, {});
    return { ...acc, ...widgetValues };
  }, {});
};

/*
 * Return an object containing fields and sections to be watched while filling the form.
 * fields and sections rules must be disable/enabled by its visibility in a given moment based on the form values.
 * E.g. If a form field has a checkbox whose value is false, and we want to hide a related input field,
 * the validation rules associated with that input field are removed.
 * The application will react only if one of these fields is changing to avoid unnecessary re-rendering.
 * @param sections: TSectionRendererProps,
 * @return object containing fields and section names to watch while typing in the form
 */
export const extractVisibilityRules = (sections: TSectionRendererProps[]) => {
  const widgetsToWatch: Record<string, unknown> = {};
  const sectionsToWatch: Record<string, unknown> = {};
  const fieldToWatch: string[] = [];
  const fieldsWithDependencies: {
    [fieldName: string]: string[];
  } = {};
  return (sections || []).reduce((acc, curr) => {
    const widgetRules = (curr.widgets ?? []).reduce<Record<string, unknown>>(
      (wAcc, wCurr) => {
        const rule = wCurr.visibility;
        if (wCurr.dependsOn) {
          fieldToWatch.push(wCurr.name);
          wCurr.dependsOn.forEach(fieldDependency => {
            fieldToWatch.push(fieldDependency);
            const previousFieldDependencies =
              fieldsWithDependencies[fieldDependency] || [];
            fieldsWithDependencies[fieldDependency] = [
              ...previousFieldDependencies,
              wCurr.name,
            ];
          });
        }
        if (!rule) {
          return wAcc;
        }
        if (isSimpleRule(rule)) {
          const name = rule?.if;
          if (rule && name) {
            wAcc[name] = rule;
            widgetsToWatch[name] = rule;
            fieldToWatch.push(name);
          }
          return wAcc;
        } else {
          rule.conditions.rules.forEach(r => {
            const name = r?.if;
            if (r && name) {
              wAcc[name] = r;
              widgetsToWatch[name] = r;
              fieldToWatch.push(name);
            }
          });
          return wAcc;
        }
      },
      {},
    );
    if (curr.visibility) {
      if (isSimpleRule(curr.visibility)) {
        const name = curr.visibility.if;
        const rule = curr.visibility;
        if (rule && name) {
          acc[name] = rule;
          sectionsToWatch[curr.id] = rule;
          fieldToWatch.push(name);
        }
      } else {
        curr.visibility.conditions.rules.forEach(r => {
          const name = r.if;
          if (r && name) {
            acc[name] = r;
            sectionsToWatch[curr.id] = r;
            fieldToWatch.push(name);
          }
        });
      }
    }
    return {
      ...acc,
      ...widgetRules,
      fieldToWatch,
      sectionsToWatch,
      widgetsToWatch,
      fieldsWithDependencies,
    };
  }, {} as FormObject);
};

/*
 * Return a value that determine if an object (section or widget) is visible in a given moment,
 * based not on its own status and value but on the value of a related field indicated in the `if` field.
 * Given a set of operators the rule is evaluated with the dependent field value.
 * It return the value of the then or the otherwise field.
 *
 * @param rule: RuleObjectType,
 * @return currentValue: any. The value of the dependent field.
 */
 /**
 *
 *
 * @template R
 * @template V
 * @param {RuleSimple<R, V>} rule
 * @param {V} ruleValue
 * @param {OperatorsMap} ruleOperator
 * @param {*} currentValue
 * @returns  {boolean}
 */
export const evaluateSingleRuleObject = <V>(
  ruleValue: V,
  ruleOperator: RuleOperators,
  invertRule: boolean,
  currentValue: FormObject | string | string[] | number,
): boolean => {
  let res: unknown;

  // Array data type.
  if (
    ruleOperator === "includesExact" ||
    ruleOperator === "includesSome" ||
    ruleOperator === "includesEvery" ||
    ruleOperator === "empty"
  ) {
    const candidates = Array.isArray(ruleValue) ? ruleValue : [ruleValue];
    if (ruleOperator === "includesEvery" || ruleOperator === "includesExact") {
      res = candidates.every(v =>
        Array.isArray(currentValue)
          ? currentValue.includes(v)
          : currentValue === v,
      );
    } else if (ruleOperator === "includesSome") {
      res = candidates.some(v =>
        Array.isArray(currentValue)
          ? currentValue.includes(v)
          : currentValue === v,
      );
    } else {
      // its is the empty rule.
      res = Array.isArray(candidates) && candidates.length === 0;
    }
  } else if (ruleOperator === "emptyString") {
    res = !currentValue;
  } else if (ruleOperator === "greater") {
    res = currentValue > ruleValue;
  } else if (ruleOperator === "greaterThan") {
    res = currentValue >= ruleValue;
  } else if (ruleOperator === "less") {
    res = currentValue < ruleValue;
  } else if (ruleOperator === "lessThan") {
    res = currentValue <= ruleValue;
  } else if (ruleOperator === "equal") {
    res = currentValue === ruleValue;
  } else if (ruleOperator === "notEqual") {
    res = currentValue !== ruleValue;
  } else if (ruleOperator === "isNull") {
    res = currentValue === null || currentValue === undefined;
  } else if (ruleOperator === "notNull") {
    res = currentValue !== null && currentValue !== undefined;
  }
  if (invertRule) {
    res = !res;
  }
  if (res) {
    return true;
  }
  return false;
};

export const evaluateRuleObject = <V, R>(
  rule: RuleObjectType<V, R>,
  currentValue: FormObject | string | string[] | number,
) => {
  if (isSimpleRule(rule)) {
    const result = evaluateSingleRuleObject(
      rule.value,
      rule.operator,
      !!rule.invert,
      currentValue,
    );
    return result ? rule.then : rule.otherwise;
  }
  const op = rule.conditions.operator;
  const rules: RuleSimple<V, R>[] = rule.conditions.rules.map(r => ({
    ...r,
    otherwise: rule.otherwise,
    then: rule.then, //NOSONAR
  }));
  const evaluations = rules.map(r =>
    evaluateSingleRuleObject(r.value, r.operator, !!r.invert, currentValue),
  );
  let result;
  switch (op) {
    case "oneOf":
      result = evaluations.filter(value => value).length === 1;
      break;
    case "allOf":
      result = evaluations.every(value => value);
      break;
    case "anyOf":
      result = evaluations.some(value => value);
      break;
  }
  return result ? rule.then : rule.otherwise;
};

export const isVisibleByRule = <V, R>(
  visibilityRules?: RuleObjectType<V, R>,
  values?: Record<string, unknown>,
) => {
  if (visibilityRules && values) {
    const rule = visibilityRules;
    if (isSimpleRule(rule)) {
      const relatedField = rule.if;
      const updatedValue = values[relatedField];
      if (updatedValue) {
        const result = evaluateRuleObject(
          visibilityRules,
          updatedValue as FormObject,
        );
        return result ? rule.then : rule.otherwise;
      } else {
        return false;
      }
    } else {
      const op = rule.conditions.operator;
      const evaluations = rule.conditions.rules.map(r => {
        const relatedField = r.if;
        const updatedValue = values[relatedField];
        return evaluateRuleObject(
          {
            ...r,
            then: rule.then,
            otherwise: rule.otherwise,
          },
          updatedValue as FormObject,
        );
      });
      let result;
      switch (op) {
        case "oneOf":
          result = evaluations.filter(value => value).length === 1;
          break;
        case "allOf":
          result = evaluations.every(value => value);
          break;
        case "anyOf":
          result = evaluations.some(value => value);
          break;
      }
      return result ? rule.then : rule.otherwise;
    }
  }
  return true;
};
/*
 * Check whether a section is visible bse on its visibility rules.
 * @see evaluateRuleObject.
 */
export const isSectionVisible = (
  section: TSectionRendererProps,
  values: Record<string, unknown>,
) => {
  return isVisibleByRule(section.visibility, values);
};

/*
 * Check whether a widget is visible bse on its visibility rules.
 * @see evaluateRuleObject.
 */
export const isWidgetVisible = (
  widget: HFieldProps,
  values: Record<string, unknown> | undefined,
) => {
  return isVisibleByRule(widget.visibility, values);
};

/**
 * Given a PageContent object this function return the same object
 * containing sections and widgets whose visibility is TRUE, mapping their values with the
 * appropriate values object passed.
 * @param pageContent: PageContent,
 * @param filterByVisibility: if true, remove the fields that are hidden in the form.
 * @param values: data object return from a form submission. E.g. { name: "jack", age: 13 }.
 */
export const buildJsonFromValues = ({
  pageContent,
  filterByVisibility = false,
  values,
}: {
  pageContent: PageContent;
  values: Record<string, unknown>;
  filterByVisibility?: boolean;
}): PageContent => {
  return {
    ...pageContent,
    step: {
      ...pageContent.step,
      sections: [...(pageContent?.step?.sections ?? [])]
        .filter(s =>
          filterByVisibility
            ? s.sendIfHidden ?? isSectionVisible(s, values)
            : true,
        )
        .map(s => ({
          ...s,
          widgets: [...(s.widgets ?? [])]
            .filter(w =>
              filterByVisibility
                ? w.sendIfHidden ?? isWidgetVisible(w, values)
                : true,
            )
            .map(w => {
              const value = values[w.name] as string;
              const shouldBeVisibleWidget = isWidgetVisible(w, values);
              const shouldBeVisibleSection = isSectionVisible(s, values);
              const shouldNotBeVisible =
                !shouldBeVisibleWidget || !shouldBeVisibleSection;
              const res: HFieldProps = {
                ...w,
              };
              if (value) {
                res.value = shouldNotBeVisible ? null : value;
              }
              return res;
            }),
        })),
    },
  };
};

/**
 * Given a PageContent object this function return a new PageContent object with sections and widgets
 * Only widgets whose visibility is TRUE are retained and their values and names are mapped out.
 * All other property are stripped away.
 * @param pageContent: PageContent,
 * @param filterByVisibility: if tru, remove fields that are hidden in the form.
 * @param values: data object return from a form submission. E.g. { name: "jack", age: 13 }.
 */
export const buildStrippedJsonFromValues = ({
  pageContent,
  filterByVisibility = false,
  values,
}: {
  pageContent: PageContent;
  values: Record<string, unknown>;
  filterByVisibility?: boolean;
}) => {
  const { analytics, ...rest } = pageContent;
  return {
    ...rest,
    step: {
      stepInfo: pageContent.step.stepInfo,
      sections: [...(pageContent?.step?.sections ?? [])]
        .filter(s =>
          filterByVisibility
            ? s.sendIfHidden ?? isSectionVisible(s, values)
            : true,
        )
        .map(s => ({
          id: s.id,
          widgets: (s.widgets ?? [])
            .filter(
              w =>
                widgetIsFormField(w) &&
                (filterByVisibility
                  ? w.sendIfHidden ?? isWidgetVisible(w, values)
                  : true),
            )
            .map(w => {
              const value = values[w.name] as string;
              const shouldBeVisibleWidget = isWidgetVisible(w, values);
              const shouldBeVisibleSection = isSectionVisible(s, values);
              const shouldNotBeVisible =
                !shouldBeVisibleWidget || !shouldBeVisibleSection;
              const res: HFieldProps = { ...w };
              res.value = shouldNotBeVisible ? null : value;
              return {
                name: res.name,
                value: res.value,
              };
            }),
        })),
    },
  };
};
/**
 * Strip all values from a form submission based on the visibility configured in the given PageContent json data
 * @param pageContent: PageContent,
 * @param filterByVisibility: if tru, remove fields that are hidden in the form.
 * @param values: data object return from a form submission. E.g. { name: "jack", age: 13 }.
 */
export const stripValuesFromPageContent = ({
  pageContent,
  filterByVisibility = false,
  values,
}: {
  pageContent: PageContent;
  values: Record<string, unknown>;
  filterByVisibility?: boolean;
}): Record<string, unknown> => {
  return (pageContent?.step?.sections ?? [])
    .filter(s => (filterByVisibility ? isSectionVisible(s, values) : true))
    .reduce<Record<string, unknown>>((secAcc, currSec) => {
      (currSec.widgets ?? [])
        .filter(w =>
          filterByVisibility
            ? isWidgetVisible(w, values) && widgetIsFormField(w)
            : widgetIsFormField(w),
        )
        .forEach(element => {
          secAcc[element.name] = values[element.name];
        });
      return secAcc;
    }, {} as Record<string, unknown>);
};

export const errorMessageStyle = (
  isSectionTransparent?: boolean,
  isTransparent?: boolean,
): HErrorMessageProps["sx"] => {
  return {
    ml: theme => (isSectionTransparent ? theme.spacing(0) : theme.spacing(5)),
    mt: theme => (isTransparent ? theme.spacing(6) : theme.spacing(0)),
    mb: theme => (isTransparent ? theme.spacing(0) : theme.spacing(4)),
  };
};
